/**
 * P0 PRODUCTION-GRADE: Centralized Error Handlers
 * ================================================
 * 
 * RÈGLES NON NÉGOCIABLES:
 * 1. Aucune logique UX basée sur message.includes()
 * 2. Aucune erreur backend ne doit être masquée sans classification
 * 3. Une action utilisateur (publish/test) doit être bloquée si le backend échoue réellement
 * 4. Les erreurs transitoires doivent être distinguées des erreurs actionnables
 * 
 * Logique basée UNIQUEMENT sur signaux backend:
 * - is_actionable: true/false
 * - transient_hint: true/false
 * - error_reason: string
 * - debug_info.suspect: string
 */

import React from 'react';
import { classify405Error, HTTP405_CLASSIFICATION } from './http405Handler';
import { 
  formatScopeAuthorityError, 
  isMetaOAuthError, 
  buildMetaOAuthToast,
  buildStructuredToast,
  SCOPE_AUTHORITY_ERROR_CODES
} from './api';

// ============================================================================
// P0: HYSTERESIS TTL - Anti flip-flop
// ============================================================================

const HYSTERESIS_TTL_MS = 10 * 60 * 1000; // 10 minutes

export const IntegrationStateManager = {
  _lastKnownGood: null,
  _lastKnownGoodState: null,
  
  setState(state) {
    if (state === 'OK') {
      this._lastKnownGood = Date.now();
      this._lastKnownGoodState = state;
    } else {
      if (!this.isWithinHysteresis()) {
        this._lastKnownGoodState = state;
      }
    }
  },
  
  getState() {
    if (this.isWithinHysteresis()) {
      return 'OK';
    }
    return this._lastKnownGoodState;
  },
  
  isWithinHysteresis() {
    if (!this._lastKnownGood) return false;
    return (Date.now() - this._lastKnownGood) < HYSTERESIS_TTL_MS;
  },
  
  reset() {
    this._lastKnownGood = null;
    this._lastKnownGoodState = null;
  },
  
  getDebugInfo() {
    return {
      lastKnownGood: this._lastKnownGood ? new Date(this._lastKnownGood).toISOString() : null,
      lastKnownGoodState: this._lastKnownGoodState,
      isWithinHysteresis: this.isWithinHysteresis(),
      hysteresisTtlMs: HYSTERESIS_TTL_MS
    };
  }
};

// ============================================================================
// SUSPECTS SILENCIEUX (405 uniquement)
// ============================================================================

const SILENT_405_SUSPECTS = [
  'safari_prefetch',
  'bot',
  'crawler_bot',
  'link_preview'
];

// ============================================================================
// ERREURS TRANSITOIRES (identifiées par error_reason du backend)
// ============================================================================

const TRANSIENT_ERROR_REASONS = [
  'meta_down',
  'meta_timeout'
];

/**
 * P0 PRODUCTION-GRADE: Gestionnaire d'erreurs centralisé.
 * 
 * Logique STRICTE basée sur signaux backend (AUCUNE logique sur message.includes()):
 * 
 * 1. 405 Errors:
 *    - RÈGLE 1: Si backend dit is_actionable=false → ignorer silencieusement
 *    - RÈGLE 2: Si method !== 'POST' ET suspect in SILENT_405_SUSPECTS → ignorer
 *    - RÈGLE 3: Sinon afficher toast avec request_id (c'est un client_bug)
 * 
 * 2. Autres erreurs:
 *    - Si is_actionable === false: ignorer silencieusement (poll/diag)
 *    - Si transient_hint === true: toast info non-bloquant
 *    - Sinon: toast erreur bloquant avec request_id + "Copier Debug Bundle"
 * 
 * @param {Error} error - L'erreur axios
 * @param {Object} toast - L'instance toast de sonner
 * @param {Object} options - Options
 * @returns {boolean} true si erreur gérée
 */
export function handleActionError(error, toast, options = {}) {
  const { context = 'ACTION', onCopyDebugBundle } = options;
  
  const httpStatus = error?.response?.status;
  const errorData = error?.response?.data || error?.data || {};
  const errorCode = errorData?.error_code || errorData?.error;
  const requestId = errorData?.request_id || 'unknown';
  const stackHash = errorData?.stack_hash;
  
  // Signaux contractuels du backend - SOURCE DE VÉRITÉ UNIQUE
  const isActionable = errorData?.is_actionable ?? true;
  const transientHint = errorData?.transient_hint ?? false;
  const retryAfterMs = errorData?.retry_after_ms;
  const errorReason = errorData?.error_reason;
  const debugInfo = errorData?.debug_info || {};
  
  // Log pour audit
  console.info(
    `[handleActionError] context=${context} httpStatus=${httpStatus} errorCode=${errorCode} ` +
    `isActionable=${isActionable} transientHint=${transientHint} errorReason=${errorReason} ` +
    `requestId=${requestId}`
  );
  
  // ========================================================================
  // ÉTAPE 1: Vérifier si c'est une erreur 405
  // ========================================================================
  if (httpStatus === 405) {
    const method = debugInfo?.method || 'GET';
    const suspect = debugInfo?.suspect;
    
    // RÈGLE P0 POINT 1: Si backend dit explicitement is_actionable=false → ignorer
    // Ceci couvre les requêtes prefetch/bot détectées côté backend
    if (isActionable === false) {
      console.info(`[handleActionError] 405 ignoré: is_actionable=false (backend signal)`);
      return true;
    }
    
    // RÈGLE COMPLÉMENTAIRE: Ignorer si method !== 'POST' ET suspect est un prefetch/bot
    // Ceci couvre les cas où le backend n'a pas pu classifier mais c'est clairement pas user
    if (method !== 'POST' && SILENT_405_SUSPECTS.includes(suspect)) {
      console.info(`[handleActionError] 405 ignoré: method=${method} + suspect=${suspect}`);
      return true;
    }
    
    // RÈGLE 3: Afficher toast pour 405 client_bug POST (vrai bug client)
    toast.error(
      <ErrorToastContent
        title="Erreur de méthode HTTP"
        message={errorData?.message || 'Méthode POST requise'}
        requestId={requestId}
        errorCode={errorCode}
        onCopyDebugBundle={onCopyDebugBundle}
      />,
      { duration: 10000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 2: Vérifier is_actionable
  // Si false, c'est un poll automatique - ignorer silencieusement
  // ========================================================================
  if (!isActionable) {
    console.info(`[handleActionError] Erreur ignorée (is_actionable=false): ${errorCode}`);
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 3: META_UNAVAILABLE - Erreur transitoire avec auto-retry
  // Auto-retry UNIQUEMENT pour META_UNAVAILABLE (jamais REAUTH/PERMISSION_DENIED)
  // ========================================================================
  const isMetaUnavailable = errorCode === 'META_UNAVAILABLE' || 
                            errorData?.blocking_reason === 'META_UNAVAILABLE' ||
                            errorData?.is_transient === true;
  
  if (isMetaUnavailable && errorData?.auto_retry_allowed !== false) {
    const retryAfter = errorData?.retry_after_ms || 5000;
    const isTransient = errorData?.is_transient || transientHint;
    
    toast.info(
      <MetaTransientToast 
        retryAfterMs={retryAfter}
        requestId={requestId}
        isTransient={isTransient}
      />,
      { duration: Math.max(6000, retryAfter + 2000) }
    );
    
    return {
      handled: true,
      isTransient: true,
      autoRetryAllowed: true,
      retryAfterMs: retryAfter,
      retryBackoffMs: errorData?.retry_backoff_ms || [5000, 15000, 30000],
      maxRetries: errorData?.max_retries || 3
    };
  }
  
  // ========================================================================
  // ÉTAPE 3.1: transient_hint générique (non-Meta)
  // ========================================================================
  if (transientHint && !isMetaUnavailable) {
    const retryText = retryAfterMs 
      ? `Réessayez dans ${Math.ceil(retryAfterMs / 1000)}s`
      : 'Réessayez dans quelques instants';
    
    toast.info(
      <div className="space-y-1">
        <div className="font-medium">Erreur temporaire</div>
        <div className="text-xs opacity-80">
          {errorReason === 'meta_down' && "L'API Meta est temporairement indisponible."}
          {errorReason === 'meta_timeout' && "L'API Meta n'a pas répondu à temps."}
          {!['meta_down', 'meta_timeout'].includes(errorReason) && errorData?.message}
        </div>
        <div className="text-xs opacity-60">{retryText}</div>
        <div className="text-xs font-mono opacity-50">ID: {requestId}</div>
      </div>,
      { duration: 6000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 3.5: REAUTH_REQUIRED - P0 FIX pour AttributeError('OAUTH_REQUIRED')
  // Traitement dédié pour réauth Instagram AVANT les autres erreurs Meta
  // IMPORTANT: Jamais de auto-retry pour REAUTH_REQUIRED
  // ========================================================================
  if (errorCode === 'REAUTH_REQUIRED' || errorReason === 'oauth_required') {
    const integration = debugInfo?.integration || 'instagram';
    
    toast.error(
      <div className="space-y-2" data-testid="reauth-required-toast">
        <div className="font-semibold text-amber-400">🔑 Reconnexion requise</div>
        <div className="text-sm">
          Votre connexion {integration.charAt(0).toUpperCase() + integration.slice(1)} doit être renouvelée.
        </div>
        <div className="text-xs opacity-80">
          Cette erreur survient lorsque les tokens ont expiré ou ont été révoqués.
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <a 
            href="/settings/integrations" 
            className="text-xs px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 rounded text-amber-300 text-center"
            data-testid="reauth-reconnect-btn"
          >
            ⚙️ Aller aux Paramètres
          </a>
          <button 
            onClick={() => window.location.href = `/settings/support/diagnostics?open=${requestId}`}
            className="text-xs underline opacity-70 hover:opacity-100"
            data-testid="reauth-view-diagnostic-btn"
          >
            🔍 Voir diagnostic
          </button>
        </div>
        <div className="text-xs font-mono opacity-50 mt-1">ID: {requestId}</div>
      </div>,
      { duration: 15000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 3.6: NO_CREDENTIALS / OAUTH_REQUIRED de publish guard
  // Instagram non connecté - CTA pour connecter
  // ========================================================================
  if (errorCode === 'no_credentials' || errorCode === 'OAUTH_REQUIRED' || errorCode === 'oauth_required') {
    const actionCta = error?.response?.data?.action_cta || 'Connecter Instagram';
    const actionRequired = error?.response?.data?.action_required;
    const noCredentialsMessage = errorData?.message || "Connectez votre compte Instagram pour publier.";
    
    toast.error(
      <div className="space-y-2" data-testid="no-credentials-toast">
        <div className="font-semibold text-blue-400">📱 Instagram non connecté</div>
        <div className="text-sm">
          {noCredentialsMessage}
        </div>
        {debugInfo && (
          <div className="text-xs opacity-70 p-2 bg-slate-800/50 rounded">
            <p>État: {debugInfo.integration_state || 'N/A'}</p>
            <p>Credentials: {debugInfo.credentials_found ? 'Oui' : 'Non'}</p>
            <p>OAuth OK: {debugInfo.oauth_ok ? 'Oui' : 'Non'}</p>
          </div>
        )}
        <div className="flex flex-col gap-2 mt-2">
          <a 
            href="/settings/integrations" 
            className="text-xs px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-300 text-center"
            data-testid="connect-instagram-btn"
          >
            ⚙️ {actionCta}
          </a>
          <button 
            onClick={() => window.location.href = `/settings/support/diagnostics?open=${requestId}`}
            className="text-xs underline opacity-70 hover:opacity-100"
            data-testid="no-credentials-view-diagnostic-btn"
          >
            🔍 Voir diagnostic
          </button>
        </div>
        <div className="text-xs font-mono opacity-50 mt-1">ID: {requestId}</div>
      </div>,
      { duration: 12000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 3.7: PERMISSION_DENIED (HTTP 403) - GARDE-FOU #3
  // Permission refusée par Meta - distinct de REAUTH_REQUIRED (401)
  // ========================================================================
  if (errorCode === 'permission_denied' || errorCode === 'PERMISSION_DENIED') {
    const actionCta = error?.response?.data?.action_cta || 'Vérifier les permissions';
    const permissionDeniedMessage = errorData?.message || "Accès refusé par Meta. L'application n'a pas les permissions requises.";
    
    toast.error(
      <div className="space-y-2" data-testid="permission-denied-toast">
        <div className="font-semibold text-orange-400">⛔ Permission refusée (403)</div>
        <div className="text-sm">
          {permissionDeniedMessage}
        </div>
        <div className="text-xs opacity-80 p-2 bg-orange-900/30 rounded">
          <p className="font-medium">Différent d&apos;une expiration de token:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>L&apos;application Meta peut nécessiter une révision</li>
            <li>Les permissions peuvent avoir été révoquées</li>
            <li>Essayez de reconnecter avec toutes les permissions</li>
          </ul>
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <a 
            href="/settings/integrations" 
            className="text-xs px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 rounded text-orange-300 text-center"
            data-testid="permission-denied-settings-btn"
          >
            ⚙️ {actionCta}
          </a>
          <button 
            onClick={() => window.location.href = `/settings/support/diagnostics?open=${requestId}`}
            className="text-xs underline opacity-70 hover:opacity-100"
            data-testid="permission-denied-diagnostic-btn"
          >
            🔍 Voir diagnostic
          </button>
        </div>
        <div className="text-xs font-mono opacity-50 mt-1">ID: {requestId}</div>
      </div>,
      { duration: 15000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 4: Vérifier si erreur Meta OAuth (190, etc.)
  // ========================================================================
  const metaError = isMetaOAuthError(error);
  if (metaError) {
    const metaToast = buildMetaOAuthToast(metaError);
    toast.error(
      <div className="space-y-2">
        <div className="font-semibold text-red-400">🔑 {metaToast.title}</div>
        <div className="text-sm">{metaToast.description}</div>
        <div className="text-xs opacity-80">
          Code: {metaToast.errorCode} | Stage: {metaToast.stage}
        </div>
        <div className="text-xs font-mono opacity-60">ID: {requestId}</div>
        <div className="text-xs mt-2 p-2 bg-orange-900/50 rounded">
          ⚠️ Reconnexion obligatoire: Déconnectez puis reconnectez Instagram
        </div>
        {onCopyDebugBundle && (
          <button 
            onClick={onCopyDebugBundle}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            📋 Copier Debug Bundle
          </button>
        )}
      </div>,
      { duration: 15000 }
    );
    return true;
  }
  
  // ========================================================================
  // ÉTAPE 5: Erreur actionnable non-transitoire - Toast bloquant complet
  // ========================================================================
  const scopeError = formatScopeAuthorityError(error);
  const message = scopeError.message || errorData?.message || 'Erreur interne';
  const stage = errorData?.stage || scopeError.stage || 'UNKNOWN';
  
  toast.error(
    <ErrorToastContent
      title={message}
      message={`Code: ${errorCode || 'UNKNOWN'} | Stage: ${stage}`}
      requestId={requestId}
      errorCode={errorCode}
      stackHash={stackHash}
      errorReason={errorReason}
      onCopyDebugBundle={onCopyDebugBundle}
    />,
    { duration: 12000 }
  );
  
  return true;
}

/**
 * Composant Toast d'erreur standardisé
 * P0 DIAGNOSTICS: Inclut le bouton "Voir diagnostic"
 */
function ErrorToastContent({ title, message, requestId, errorCode, stackHash, errorReason, onCopyDebugBundle }) {
  const handleViewDiagnostic = () => {
    // Navigate to diagnostics page with the request_id
    window.location.href = `/settings/support/diagnostics?open=${requestId}`;
  };
  
  return (
    <div className="space-y-2">
      <div className="font-semibold">{title}</div>
      <div className="text-xs opacity-80">{message}</div>
      {errorReason && (
        <div className="text-xs opacity-70">Raison: {errorReason}</div>
      )}
      <div className="text-xs font-mono opacity-60">
        ID: {requestId}
        {stackHash && ` | Hash: ${stackHash}`}
      </div>
      <div className="flex gap-2 mt-2">
        {requestId && requestId !== 'unknown' && (
          <button 
            onClick={handleViewDiagnostic}
            className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white"
            data-testid="view-diagnostic-btn"
          >
            🔍 Voir diagnostic
          </button>
        )}
        {onCopyDebugBundle && (
          <button 
            onClick={onCopyDebugBundle}
            className="text-xs px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-white"
          >
            📋 Copier
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Vérifie si une erreur 405 doit être ignorée.
 */
export function shouldIgnore405(error) {
  const httpStatus = error?.response?.status;
  if (httpStatus !== 405) return false;
  
  const debugInfo = error?.response?.data?.debug_info || {};
  const method = debugInfo?.method || 'GET';
  const suspect = debugInfo?.suspect;
  
  return method !== 'POST' || SILENT_405_SUSPECTS.includes(suspect);
}

/**
 * Composant Toast pour erreurs Meta transitoires
 * Affiche un countdown et tag "transient"
 */
function MetaTransientToast({ retryAfterMs, requestId, isTransient }) {
  const [countdown, setCountdown] = React.useState(Math.ceil(retryAfterMs / 1000));
  
  React.useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);
  
  return (
    <div className="space-y-2" data-testid="meta-transient-toast">
      <div className="flex items-center gap-2">
        <span className="font-medium text-yellow-400">⏳ Meta indisponible</span>
        {isTransient && (
          <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-300 rounded" data-testid="transient-tag">
            transient
          </span>
        )}
      </div>
      <div className="text-sm">
        L&apos;API Meta est temporairement indisponible.
      </div>
      <div className="text-xs opacity-80">
        {countdown > 0 
          ? `Réessayez dans ${countdown}s...` 
          : 'Vous pouvez réessayer maintenant'}
      </div>
      <div className="text-xs font-mono opacity-50">ID: {requestId}</div>
    </div>
  );
}

/**
 * Auto-retry avec backoff exponentiel pour META_UNAVAILABLE uniquement.
 * 
 * @param {Function} apiCall - La fonction d'appel API à réessayer
 * @param {Object} toast - Instance toast de sonner
 * @param {Object} options - Options de retry
 * @returns {Promise<Object>} Résultat de l'appel
 */
export async function safeActionCallWithRetry(apiCall, toast, options = {}) {
  const { 
    maxRetries = 3, 
    backoffMs = [5000, 15000, 30000],
    context = 'ACTION',
    onRetry = null 
  } = options;
  
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiCall();
      IntegrationStateManager.setState('OK');
      return { success: true, data: response.data, attempts: attempt + 1 };
    } catch (error) {
      lastError = error;
      const errorData = error?.response?.data || {};
      
      // Vérifier si auto-retry est permis (META_UNAVAILABLE uniquement)
      const autoRetryAllowed = errorData?.auto_retry_allowed === true;
      const isTransient = errorData?.is_transient === true || 
                          errorData?.blocking_reason === 'META_UNAVAILABLE';
      
      // RÈGLE STRICTE: Jamais de retry pour REAUTH/PERMISSION_DENIED
      const isAuthError = ['REAUTH_REQUIRED', 'PERMISSION_DENIED', 'OAUTH_REQUIRED', 
                          'oauth_required', 'permission_denied', 'needs_reconnect']
                          .includes(errorData?.error_code || errorData?.blocking_reason);
      
      if (isAuthError) {
        console.info(`[safeActionCallWithRetry] No retry for auth error: ${errorData?.error_code}`);
        handleActionError(error, toast, { context });
        return { success: false, error, attempts: attempt + 1, noRetry: 'auth_error' };
      }
      
      // Si pas transient ou dernier essai → ne pas réessayer
      if (!isTransient || !autoRetryAllowed || attempt >= maxRetries) {
        console.info(`[safeActionCallWithRetry] No more retries: transient=${isTransient} allowed=${autoRetryAllowed} attempt=${attempt}`);
        handleActionError(error, toast, { context });
        return { success: false, error, attempts: attempt + 1 };
      }
      
      // Calculer le délai de backoff
      const delayMs = backoffMs[Math.min(attempt, backoffMs.length - 1)];
      console.info(`[safeActionCallWithRetry] Retry ${attempt + 1}/${maxRetries} in ${delayMs}ms (META_UNAVAILABLE)`);
      
      // Callback optionnel
      if (onRetry) {
        onRetry({ attempt: attempt + 1, maxRetries, delayMs });
      }
      
      // Toast info pour le retry
      toast.info(
        <div className="space-y-1">
          <div className="font-medium text-yellow-400">🔄 Nouvelle tentative...</div>
          <div className="text-xs">
            Tentative {attempt + 2}/{maxRetries + 1} dans {delayMs / 1000}s
          </div>
          <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-300 rounded inline-block mt-1">
            transient
          </span>
        </div>,
        { duration: delayMs }
      );
      
      // Attendre avant le retry
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  // Si on arrive ici, toutes les tentatives ont échoué
  handleActionError(lastError, toast, { context });
  return { success: false, error: lastError, attempts: maxRetries + 1 };
}

/**
 * Wrapper pour les appels API d'action (version simple sans retry).
 */
export async function safeActionCall(apiCall, toast, options = {}) {
  try {
    const response = await apiCall();
    IntegrationStateManager.setState('OK');
    return { success: true, data: response.data };
  } catch (error) {
    handleActionError(error, toast, options);
    return { success: false, error };
  }
}

// ============================================================================
// P0 FIX: SAFE ERROR SERIALIZATION
// ============================================================================

/**
 * P0 FIX: Converts any error (including Axios errors with circular references) 
 * to a safely serializable object.
 * 
 * NEVER includes: err.request, err.config, XMLHttpRequest, or any circular refs.
 * 
 * @param {Error|any} err - The error object to convert
 * @param {Object} options - Optional configuration
 * @param {number} options.maxDataLength - Max length for response data (default: 2000)
 * @returns {Object} A safely serializable error object
 */
export function toSafeError(err, options = {}) {
  const { maxDataLength = 2000 } = options;
  
  // Handle null/undefined
  if (!err) {
    return { message: 'Unknown error', name: 'Error' };
  }
  
  // Handle string errors
  if (typeof err === 'string') {
    return { message: err, name: 'Error' };
  }
  
  // Extract response data safely
  let responseData = null;
  let responseStatus = null;
  let responseHeaders = null;
  
  if (err.response) {
    responseStatus = err.response.status;
    
    // Extract only safe headers (no cookies, auth tokens)
    if (err.response.headers) {
      responseHeaders = {
        'content-type': err.response.headers['content-type'],
        'x-request-id': err.response.headers['x-request-id'],
        'cache-control': err.response.headers['cache-control']
      };
    }
    
    // Safely extract response data with size limit
    if (err.response.data) {
      try {
        // P0 FIX: Use safeJsonStringify to handle potential circular refs in data
        const dataStr = typeof err.response.data === 'string' 
          ? err.response.data 
          : safeJsonStringify(err.response.data, maxDataLength);
        
        // Check if stringify failed
        if (dataStr.includes('[Circular]') || dataStr.includes('[Stringify Error')) {
          // Fallback to extracting key fields only
          responseData = {
            ok: err.response.data?.ok,
            error: err.response.data?.error,
            error_code: err.response.data?.error_code,
            message: err.response.data?.message,
            action_required: err.response.data?.action_required,
            request_id: err.response.data?.request_id,
            http_status: err.response.data?.http_status,
            _had_circular_refs: true
          };
        } else {
          responseData = dataStr.length > maxDataLength 
            ? { _truncated: true, _length: dataStr.length }
            : err.response.data;
        }
      } catch (stringifyErr) {
        // If data itself has circular refs, extract key fields only
        responseData = {
          ok: err.response.data?.ok,
          error: err.response.data?.error,
          error_code: err.response.data?.error_code,
          message: err.response.data?.message,
          action_required: err.response.data?.action_required,
          request_id: err.response.data?.request_id,
          _stringify_error: stringifyErr.message,
          _truncated: true
        };
      }
    }
  }
  
  // Build safe error object
  const safeError = {
    // Standard Error properties
    message: err.message || String(err),
    name: err.name || 'Error',
    
    // Axios-specific properties (safe ones only)
    code: err.code, // e.g., 'ECONNREFUSED', 'ETIMEDOUT'
    status: responseStatus,
    
    // Response data (safe, size-limited)
    data: responseData,
    
    // Extracted fields for convenience
    error_code: responseData?.error_code || err.error_code,
    action_required: responseData?.action_required || err.action_required,
    request_id: responseData?.request_id || err.request_id,
    
    // Headers (only safe ones)
    headers: responseHeaders,
    
    // Stack trace (truncated for safety)
    stack: err.stack ? err.stack.split('\n').slice(0, 5).join('\n') : undefined,
    
    // Timestamp for debugging
    timestamp: new Date().toISOString()
  };
  
  // Remove undefined values for cleaner output
  Object.keys(safeError).forEach(key => {
    if (safeError[key] === undefined || safeError[key] === null) {
      delete safeError[key];
    }
  });
  
  return safeError;
}

/**
 * P0 FIX: Safe JSON.stringify that handles circular references.
 * 
 * @param {any} value - Value to stringify
 * @param {number} maxLen - Max output length (default: 10000)
 * @returns {string} JSON string, safely truncated
 */
export function safeJsonStringify(value, maxLen = 10000) {
  const seen = new WeakSet();
  
  try {
    const result = JSON.stringify(value, (key, val) => {
      // Skip known circular/problematic properties
      if (['request', 'config', 'socket', 'agent', 'res', 'req'].includes(key)) {
        return '[Circular/Omitted]';
      }
      
      // Handle circular references
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) {
          return '[Circular]';
        }
        seen.add(val);
      }
      
      // Handle functions
      if (typeof val === 'function') {
        return '[Function]';
      }
      
      return val;
    }, 2);
    
    if (result.length > maxLen) {
      return result.substring(0, maxLen) + '... [truncated]';
    }
    
    return result;
  } catch (e) {
    return `[Stringify Error: ${e.message}]`;
  }
}

/**
 * P0 FIX: Log error safely to console without circular reference issues.
 * 
 * @param {string} prefix - Log prefix (e.g., '[ConnectStep]')
 * @param {Error} error - The error to log
 * @param {Object} context - Additional context to include
 */
export function logSafeError(prefix, error, context = {}) {
  const safeErr = toSafeError(error);
  
  // P0 FIX: Merge context safely - stringify context first to catch any circular refs there too
  let safeContext = {};
  try {
    // Only include primitives from context
    Object.entries(context).forEach(([key, value]) => {
      if (value === null || ['string', 'number', 'boolean', 'undefined'].includes(typeof value)) {
        safeContext[key] = value;
      } else {
        // For objects/arrays, try to stringify them safely
        safeContext[key] = safeJsonStringify(value, 500);
      }
    });
  } catch {
    safeContext = { _context_error: true };
  }
  
  // P0 FIX: Log as a string to avoid any browser JSON.stringify issues
  const logData = {
    ...safeErr,
    ...safeContext
  };
  
  // Use safeJsonStringify to create a loggable string
  const logString = safeJsonStringify(logData, 5000);
  console.error(`${prefix}`, logString);
  
  return safeErr;
}

export default {
  handleActionError,
  shouldIgnore405,
  safeActionCall,
  safeActionCallWithRetry,
  IntegrationStateManager,
  HYSTERESIS_TTL_MS,
  toSafeError,
  safeJsonStringify,
  logSafeError
};
